import { useMemo } from 'react';

import { useFeatureFlag } from '@/app/services/features';
import { useGetStorageLocationsQuery } from '@/app/services/locations';
import { IOWA_ID } from '@/constants';
import { ColDef } from '@/grid/types';
import {
  ODSemenItem as ODSemen,
  PulledSemenItem as PulledSemen,
  ScheduledSemenItem as ScheduledSemen,
} from '@/services/semen-pull';
import { SiteOption } from '@/services/sites';

import { SEMEN_TYPES, STORAGE_LOCATIONS } from './constants';
import { CreateSemenButton } from './grid-cell-rendered-components/CreateSemenButton';
import { PulledSemenTypeCell } from './grid-cell-rendered-components/PulledSemenTypeCell';
import { ScheduledSemenTypeCell } from './grid-cell-rendered-components/ScheduledSemenTypeCell';
import { SireNoteCell } from './grid-cell-rendered-components/SireNoteCell';
import { UpdateSemenButton } from './grid-cell-rendered-components/UpdateSemenButton';

const editableStyle =
  'bg-white border-px! border-[#cccccc]! rounded-[4px] m-[0.166rem] h-[1.666rem]!';

type ScheduledSemenWithExtraLabels = ScheduledSemen & {
  extraLabels?: number | string | null;
};

const getScheduledSemenTypeName = (
  data: ScheduledSemen | undefined,
  useConventionalType = false,
) => {
  if (!useConventionalType) return data?.semenTypeName;

  if (
    data?.semenTypeName === SEMEN_TYPES.CONVENTIONAL ||
    data?.semenTypeName === 'Conventional' ||
    data?.semenTypeName === 'Unknown'
  ) {
    return SEMEN_TYPES.CONVENTIONAL;
  }

  if (
    !data?.semenTypeName &&
    data?.semenSexed === 'No' &&
    data.semenSex === 'Unknown'
  ) {
    return SEMEN_TYPES.CONVENTIONAL;
  }

  return data?.semenTypeName;
};

const isSemenIncomplete = (data: ScheduledSemen | undefined) =>
  !data?.cane || !data?.colCode || !data?.naab || !data?.semenStorageId;

export function useScheduledSemenColDefs(selectedSiteId?: string | null) {
  const isSemenConventionalTypeEnabled = useFeatureFlag(
    'Semen_Conventional_Type',
  );
  const enablePulledSemenFertSite = useFeatureFlag('Pulled_Semen_Fert_Site');

  const { data: siteStorageLocations } = useGetStorageLocationsQuery(
    {
      skip: 0,
      take: 1,
      where: { siteId: { eq: selectedSiteId }, stateCode: { eq: 0 } },
    },
    { skip: !enablePulledSemenFertSite || !selectedSiteId },
  );

  // A fert site with no storage locations of its own (TGU sites) keeps its semen
  // in another site's tank, so semenStorageSiteName can never equal its own name.
  const siteSharesAnotherSitesTank =
    enablePulledSemenFertSite &&
    !!selectedSiteId &&
    siteStorageLocations?.length === 0;

  return useMemo<ColDef<ScheduledSemen>[]>(
    () =>
      [
        {
          header: 'Edit',
          cellRenderer: 'agGroupCellRenderer',
          width: 30,
          cellStyle({ data }) {
            const dsSemenTypeName = data?.semenTypeName;
            let SemenTypeName = '';
            if (data?.semenSexed === 'No') {
              SemenTypeName = SEMEN_TYPES.CONVENTIONAL;
            } else if (
              data?.semenSexed === 'Yes' &&
              data.semenSex === 'Female'
            ) {
              SemenTypeName = SEMEN_TYPES.SEXED_FEMALE;
            } else if (data?.semenSexed === 'Yes' && data.semenSex === 'Male') {
              SemenTypeName = SEMEN_TYPES.SEXED_MALE;
            } else if (data?.semenSexed === 'Yes') {
              SemenTypeName = SEMEN_TYPES.INCOMPLETE;
            }
            if (data?.semenId) {
              if (
                ((dsSemenTypeName === SEMEN_TYPES.RS_FEMALE ||
                  dsSemenTypeName === SEMEN_TYPES.RS_MALE ||
                  dsSemenTypeName === SEMEN_TYPES.CONVENTIONAL) &&
                  SemenTypeName !== SEMEN_TYPES.CONVENTIONAL) ||
                (dsSemenTypeName === SEMEN_TYPES.SEXED_FEMALE &&
                  SemenTypeName !== SEMEN_TYPES.SEXED_FEMALE) ||
                (dsSemenTypeName === SEMEN_TYPES.SEXED_MALE &&
                  SemenTypeName !== SEMEN_TYPES.SEXED_MALE)
              ) {
                return {
                  backgroundColor: '#D71826',
                };
              }
            }
            return;
          },
        },
        {
          headerName: 'Semen (Type)',
          autoHeight: true,
          wrapText: true,

          width: 130,
          valueGetter: ({ data }) => {
            if (!data) {
              return;
            }

            const semenTypeName = getScheduledSemenTypeName(
              data,
              isSemenConventionalTypeEnabled,
            );
            const nameType = data.name_Type?.toString();

            if (nameType?.includes('()') && semenTypeName) {
              return `${data.sireName?.toString() ?? ''} (${semenTypeName})`;
            }
            if (!nameType && semenTypeName)
              return `${data.sireName ?? ''} (${semenTypeName})`;
            return nameType;
          },
          cellRenderer: ScheduledSemenTypeCell,
          tooltipValueGetter: (params) =>
            `Sire Reg Num: ${params.data?.sireRegNum}`,
          tooltipShowDelay: 0,
          cellStyle({ data, context }) {
            if (!data?.semenName) {
              return {
                backgroundColor: 'orange',
              };
            }
            if (
              context.sireDuplicates.has(data.sireId) &&
              !context.semenDuplicates.has(data.semenId)
            ) {
              return { backgroundColor: 'yellow' };
            }
            if (context.semenDuplicates.has(data.semenId)) {
              return { backgroundColor: 'pink' };
            }

            return;
          },
        },
        {
          headerName: 'D/S',
          field: 'donors_Sire',
          initialWidth: 70,
          valueGetter: ({ data, context }) => {
            if (data?.name_Type?.includes('()')) {
              return context?.unfinishedScheduledDonors?.length as number;
            }
            return data?.donors_Sire;
          },
        },
        {
          headerName: 'Ranch',
          field: 'accountName',
          width: 90,
          cellStyle({ value }) {
            if (!value) {
              return { backgroundColor: '#001FFF' };
            }

            return;
          },
        },
        {
          headerName: 'Location',
          field: 'semenStorageName',
          width: 120,
          cellStyle({ data, context }) {
            if (!data) {
              return;
            }
            const storageName = data.semenStorageName ?? '';

            if (context.selectedSite.id === IOWA_ID) {
              if (
                storageName.includes(STORAGE_LOCATIONS.VATC) ||
                storageName.includes(STORAGE_LOCATIONS.IASS_1) ||
                storageName.includes(STORAGE_LOCATIONS.IASS_2) ||
                storageName.includes(STORAGE_LOCATIONS.IASS_3)
              ) {
                return;
              } else if (storageName.includes(STORAGE_LOCATIONS.VATD)) {
                return {
                  backgroundColor: 'var(--tog-green)',
                  color: 'white',
                };
              } else if (storageName.includes(STORAGE_LOCATIONS.VATF)) {
                return {
                  backgroundColor: 'var(--tog-purple)',
                  color: 'white',
                };
              } else if (storageName.includes(STORAGE_LOCATIONS.VATE)) {
                return {
                  backgroundColor: 'var(--tog-orange)',
                  color: 'white',
                };
              } else if (storageName.includes(STORAGE_LOCATIONS.AGGIA_C)) {
                return {
                  backgroundColor: 'var(--tog-blue)',
                  color: 'white',
                };
              } else {
                return {
                  backgroundColor: 'var(--tog-red)',
                  color: 'white',
                };
              }
            }
            if (
              storageName.includes(STORAGE_LOCATIONS.DROP_TANK) ||
              storageName.includes(STORAGE_LOCATIONS.TRAVEL_TANK) ||
              storageName.includes(STORAGE_LOCATIONS.SHIPPING)
            ) {
              return {
                backgroundColor: 'var(--tog-red)',
                color: 'white',
              };
            }
            if (
              !context.selectedSite.name.includes(storageName.substring(0, 2))
            ) {
              return {
                backgroundColor: 'var(--tog-red)',
                color: 'white',
              };
            }

            return;
          },
        },
        {
          headerName: 'Cane Code',
          field: 'cane',
          initialWidth: 90,
          cellStyle({ value }) {
            if (!value) {
              return { backgroundColor: '#D71826' };
            }

            return;
          },
        },
        {
          headerName: 'Col Code',
          field: 'colCode',
          initialWidth: 90,
          cellStyle({ value }) {
            if (!value) {
              return { backgroundColor: '#D71826' };
            }

            return;
          },
        },
        { headerName: 'NAAB', field: 'naab', initialWidth: 90 },
        { headerName: 'Secondary Owner', field: 'secondaryOwner', width: 110 },
        {
          headerName: 'Available',
          field: 'quantity',
          initialWidth: 90,
          cellStyle({ data, context }) {
            if (data?.quantity === 0) {
              return { backgroundColor: '#D71826', color: 'white' };
            }
            if (context.semenDuplicates.has(data?.semenId)) {
              return { backgroundColor: 'pink' };
            }

            return;
          },
        },
        {
          headerName: 'Separate Fert',
          field: 'separateFert',
          initialWidth: 90,
        },
        {
          headerName: 'Suggested',
          field: 'suggestedToPull',
          width: 80,
          editable: true,
          singleClickEdit: true,
          cellClass: editableStyle,
        },
        {
          headerName: 'Move',
          field: 'moveNum',
          initialWidth: 80,
          editable({ data, context }) {
            if (siteSharesAnotherSitesTank) {
              return data?.semenId !== null && !isSemenIncomplete(data);
            }

            return (
              data?.semenId !== null &&
              (data?.semenStorageSiteName === context.selectedSite.name ||
                data?.accountId === null)
            );
          },
          singleClickEdit: true,
          valueSetter: (params) => {
            params.data.moveNum = params.newValue;
            return true;
          },
          cellClass: editableStyle,
          cellClassRules: {
            error(params) {
              if (
                params.data?.moveNum !== 0 &&
                (params?.data?.moveNum as number) >
                  (params?.data?.quantity as number)
              ) {
                return true;
              }
              return false;
            },
          },
          cellRenderer: (params: {
            data: ScheduledSemen;
            context: {
              selectedSite: SiteOption;
              selectedSemen: ODSemen;
              sireDuplicates: Set<string>;
              semenDuplicates: Set<string>;
            };
          }) => {
            if (
              params.data?.semenId === null &&
              params.data?.sireName !== null &&
              params.data?.accountId !== null
            ) {
              return CreateSemenButton({ data: params.data });
            } else if (siteSharesAnotherSitesTank) {
              if (
                params.data?.semenId !== null &&
                params.data?.accountId !== null &&
                isSemenIncomplete(params.data)
              ) {
                return UpdateSemenButton({ data: params.data });
              }
              if (params.data?.semenId !== null) {
                return params.data.moveNum;
              }
              return;
            } else if (
              params.data?.semenId !== null &&
              params.data.semenStorageSiteName !==
                params.context.selectedSite.name &&
              params.data?.accountId !== null
            ) {
              return UpdateSemenButton({ data: params.data });
            } else if (params.data?.semenId !== null) {
              return params.data.moveNum;
            }
            return;
          },
        },
        {
          headerName: 'Export',
          field: 'exportName',
          initialWidth: 90,
          cellStyle({ value }) {
            if (value === 'Yes') {
              return {
                backgroundColor: 'yellow',
                color: 'black',
              };
            }
          },
        },
        {
          headerName: 'Comment',
          field: 'comment',
          autoHeight: true,
          wrapText: true,
          wrapHeaderText: true,
          width: 200,
        },
        {
          headerName: 'Sire Note',
          field: 'sireNote',
          cellRenderer: SireNoteCell,
          width: 180,
        },
        {
          headerName: 'Extra Labels',
          field: 'extraLabels',
          editable({ data }) {
            if (!data) {
              return false;
            }

            return (
              !!(data.sireId || data.semenId) &&
              data.semenType != null &&
              !!data.semenOwnerId
            );
          },
          singleClickEdit: true,
          cellClass: editableStyle,
          cellClassRules: {
            error({ data }) {
              const extraLabels = data?.extraLabels;

              if (
                extraLabels === undefined ||
                extraLabels === null ||
                extraLabels === ''
              ) {
                return false;
              }

              return Number.parseInt(extraLabels.toString()) < 1;
            },
          },
          width: 85,
        } as ColDef<ScheduledSemenWithExtraLabels>,
      ] as ColDef<ScheduledSemen>[],
    [isSemenConventionalTypeEnabled, siteSharesAnotherSitesTank],
  );
}

export function usePulledSemenColDefs() {
  return useMemo(
    () =>
      [
        { header: 'Edit', cellRenderer: 'agGroupCellRenderer', width: 40 },
        {
          headerName: 'Semen (Type)',
          field: 'name_Type',
          autoHeight: true,
          wrapText: true,
          wrapHeaderText: true,
          width: 110,

          cellRenderer: PulledSemenTypeCell,
          cellStyle({ data, context }) {
            if (context.name_TypeDuplicates.has(data?.name_Type)) {
              return { backgroundColor: 'yellow' };
            }

            return;
          },
        },
        { headerName: 'Ranch', field: 'accountName', width: 100 },
        { headerName: 'Location', field: 'semenStorageName', width: 110 }, //todo check
        { headerName: 'Cane Code', field: 'cane', width: 100 },
        { headerName: 'Col Code', field: 'colCode', width: 80 },
        { headerName: 'NAAB', field: 'naab', width: 80 },
        { headerName: 'Secondary Owner', field: 'secondaryOwner', width: 110 },
        {
          headerName: 'Pulled',
          valueGetter({ data }) {
            return data?.pulledQuantity ? data.pulledQuantity : 0;
          },
          width: 90,
        },
        {
          headerName: 'Return',
          field: 'returnNum',
          editable: true,
          singleClickEdit: true,
          cellClass: editableStyle,
          cellClassRules: {
            error({ data }) {
              if (
                data?.returnNum !== 0 &&
                (data?.returnNum as number) > (data?.quantity as number)
              ) {
                return true;
              }
              return false;
            },
          },
          width: 90,
        },
      ] as ColDef<PulledSemen>[],
    [],
  );
}
